import { Link } from "@tanstack/react-router";
import { forwardRef, type AnchorHTMLAttributes, type ComponentType, type ReactNode, type Ref } from "react";
import { useLocalePath } from "@/lib/i18n/I18nProvider";

/**
 * Route ids may carry TanStack's "non-nested" suffix (e.g.
 * "/media-monitoring/platform_/faq"). That underscore is a file-convention
 * marker, never part of the public URL, so strip it from any `to` we build.
 */
function stripRouteIdUnderscores(path: string): string {
  return path.replace(/_(?=\/)/g, "").replace(/_$/, "");
}

/** Collect "$param" segment names present in a path template. */
function requiredParams(path: string): string[] {
  return path.split("/").filter((s) => s.startsWith("$") && s.length > 1).map((s) => s.slice(1));
}

/**
 * Drop-in replacement for `<Link>` that prefixes the `to` path with `/bg`
 * (or maps it via the EN_TO_BG slug map) when the user is on the BG locale.
 *
 * Only string `to` props are localized; other navigation patterns (objects,
 * relative paths) pass through unchanged. Anchor/hash-only paths (e.g. "#form")
 * also pass through unchanged.
 */
type LocaleLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to: string;
  params?: Record<string, unknown>;
  search?: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>);
  hash?: string;
  replace?: boolean;
  resetScroll?: boolean;
  preload?: false | "intent" | "viewport" | "render";
  activeProps?: Record<string, unknown> | (() => Record<string, unknown>);
  inactiveProps?: Record<string, unknown> | (() => Record<string, unknown>);
  activeOptions?: { exact?: boolean; includeHash?: boolean; includeSearch?: boolean };
  children?: ReactNode;
};

export const LocaleLink = forwardRef<HTMLAnchorElement, LocaleLinkProps>(
  function LocaleLink(props, ref) {
    const localize = useLocalePath();
    const { to, params, children, className } = props;
    const cleanTo = typeof to === "string" ? stripRouteIdUnderscores(to) : to;
    const nextTo = typeof cleanTo === "string" && cleanTo.startsWith("/") ? localize(cleanTo) : cleanTo;

    // Never emit a link with an unsubstituted "$slug" placeholder - that URL
    // 404s and gets crawled. Render the content without a link instead.
    if (typeof nextTo === "string") {
      const missing = requiredParams(nextTo).some((name) => {
        const value = params?.[name];
        return value === undefined || value === null || String(value).trim() === "";
      });
      if (missing) {
        return <span className={className}>{children}</span>;
      }
    }

    const linkProps = { ...props, to: nextTo };
    // Link's `to` is a strict union of known routes; we accept any string
    // because we localize dynamically at runtime.
    const LinkAny = Link as unknown as ComponentType<Record<string, unknown> & { ref?: Ref<HTMLAnchorElement> }>;
    return <LinkAny ref={ref} {...linkProps} />;
  },
);
